import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck, Lock } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CountrySelect } from "@/components/ui/country-select";
import { useToast } from "@/hooks/use-toast";
import { submitLead, generateResilienceReport, LeadFormData, QuizData } from "@/lib/leadService";
import {
  trackFormSubmission,
  trackConversion,
  trackQuizStart,
  trackQuizComplete,
  trackLeadSubmit,
  trackScoreComplete,
  trackScoreLeadSubmit,
} from "@/lib/analytics";
import ReactMarkdown from "react-markdown";
import { detectCountryFromTimezone, getCountryByCode } from "@/lib/countries";
import HeroFields from "@/components/HeroFields";

type Option = { label: string; score: 0 | 1 | 2 | 3 };
type Question = { id: string; q: string; options: Option[] };

const GROWTH_QUESTIONS: Question[] = [
  {
    id: "q1",
    q: "Where is your product today?",
    options: [
      { label: "Idea", score: 0 },
      { label: "Building", score: 1 },
      { label: "In market", score: 2 },
      { label: "Scaling", score: 3 },
    ],
  },
  {
    id: "q2",
    q: "How do you forecast demand?",
    options: [
      { label: "Gut feel", score: 0 },
      { label: "Spreadsheets", score: 1 },
      { label: "One planning system", score: 2 },
      { label: "Integrated planning", score: 3 },
    ],
  },
  {
    id: "q3",
    q: "What happens when you add a new SKU or feature?",
    options: [
      { label: "Something breaks", score: 0 },
      { label: "Manual scramble", score: 1 },
      { label: "Mostly smooth", score: 2 },
      { label: "Fully repeatable", score: 3 },
    ],
  },
  {
    id: "q4",
    q: "Supplier dependencies?",
    options: [
      { label: "Single source, no backup", score: 0 },
      { label: "Single source, known risk", score: 1 },
      { label: "Dual source on critical items", score: 2 },
      { label: "Resilient network", score: 3 },
    ],
  },
  {
    id: "q5",
    q: "How fast could you enter a new market?",
    options: [
      { label: "No idea", score: 0 },
      { label: "12 months or more", score: 1 },
      { label: "6 to 12 months", score: 2 },
      { label: "Under 6 months", score: 3 },
    ],
  },
  {
    id: "q6",
    q: "Where does AI sit in your product or operations today?",
    options: [
      { label: "Nowhere", score: 0 },
      { label: "Experiments", score: 1 },
      { label: "One workflow in production", score: 2 },
      { label: "Core to how we operate", score: 3 },
    ],
  },
  {
    id: "q7",
    q: "Can leadership see operations in one view?",
    options: [
      { label: "No", score: 0 },
      { label: "Partial reports", score: 1 },
      { label: "Weekly KPIs", score: 2 },
      { label: "Live view", score: 3 },
    ],
  },
  {
    id: "q8",
    q: "If demand doubled next quarter?",
    options: [
      { label: "We would break", score: 0 },
      { label: "Heavy pain", score: 1 },
      { label: "Manageable strain", score: 2 },
      { label: "Built for it", score: 3 },
    ],
  },
];

// Organization track — replaces the "where is your product" opener with the
// operating-system constraint question. The remaining questions still probe
// the operating backbone (planning, systems, supply, leadership visibility).
const OPERATIONS_QUESTIONS: Question[] = [
  {
    id: "q1",
    q: "Where is the largest constraint in your operating system today?",
    options: [
      { label: "Planning and forecasting", score: 0 },
      { label: "Disconnected systems or ERP", score: 1 },
      { label: "Fulfillment and supply chain", score: 2 },
      { label: "Leadership visibility", score: 3 },
    ],
  },
  ...GROWTH_QUESTIONS.slice(1),
];

const bandFor = (score: number) => {
  if (score <= 8) {
    return {
      band: "Fragile",
      color: "text-destructive",
      read:
        "Your operation is exposed. A single shock in demand or supply could break launches and margin. Priority: get a diagnostic view of where the fragility lives.",
    };
  }
  if (score <= 16) {
    return {
      band: "Developing",
      color: "text-steel-2",
      read:
        "The foundations are there but not yet load-bearing. You can grow, but growth will surface real gaps in planning, sourcing, or AI leverage. Priority: harden two or three critical links.",
    };
  }
  return {
    band: "Resilient",
    color: "text-primary",
    read:
      "You have a mature backbone. The next unlock is usually AI leverage on top of it: better forecasts, faster launches, sharper leadership visibility.",
  };
};

const ResilienceScore = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const track = searchParams.get("track") === "operations" ? "operations" : "founder";
  const QUESTIONS = track === "operations" ? OPERATIONS_QUESTIONS : GROWTH_QUESTIONS;
  const [phase, setPhase] = useState<"quiz" | "form" | "done" | "report">("quiz");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(Array(QUESTIONS.length).fill(null));
  const [startedTracked, setStartedTracked] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    company: "",
    email: "",
    country: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [report, setReport] = useState<{ summary: string; recommendations: string[] } | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  useEffect(() => {
    setForm((p) => ({ ...p, country: detectCountryFromTimezone() }));
  }, []);

  // Hash sync: read #q-N on mount, and reflect step changes into history
  useEffect(() => {
    const m = window.location.hash.match(/^#q-(\d+)$/);
    if (m) {
      const idx = Math.max(0, Math.min(QUESTIONS.length - 1, parseInt(m[1], 10) - 1));
      setStep(idx);
    }
    const onPop = () => {
      const mm = window.location.hash.match(/^#q-(\d+)$/);
      if (mm) {
        const idx = Math.max(0, Math.min(QUESTIONS.length - 1, parseInt(mm[1], 10) - 1));
        setStep(idx);
        setPhase("quiz");
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === "quiz") {
      const target = `#q-${step + 1}`;
      if (window.location.hash !== target) {
        window.history.replaceState(null, "", target);
      }
    }
  }, [step, phase]);

  const score = useMemo(
    () => answers.reduce((sum: number, a) => sum + (a ?? 0), 0),
    [answers],
  );
  const band = useMemo(() => bandFor(score), [score]);
  const progress = Math.round(((step + (phase === "quiz" ? 0 : 1)) / QUESTIONS.length) * 100);

  const choose = (optionIdx: number) => {
    if (!startedTracked) {
      trackQuizStart("resilience_score");
      setStartedTracked(true);
    }
    const next = [...answers];
    next[step] = QUESTIONS[step].options[optionIdx].score;
    setAnswers(next);

    if (step < QUESTIONS.length - 1) {
      const nextStep = step + 1;
      window.history.pushState(null, "", `#q-${nextStep + 1}`);
      setStep(nextStep);
    } else {
      const total = next.reduce((s: number, a) => s + (a ?? 0), 0);
      const b = bandFor(total);
      trackQuizComplete("resilience_score", b.band, total);
      trackScoreComplete("resilience_score", total, b.band);
      setPhase("form");
    }
  };

  const back = () => {
    if (step > 0) {
      window.history.back();
    }
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.fullName.trim()) e.fullName = "Required";
    if (!form.company.trim()) e.company = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!form.country) e.country = "Required";
    if (!agreed) e.agreed = "Please accept to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setReportError(null);

    const countryName = getCountryByCode(form.country)?.name || form.country;
    const summary = QUESTIONS.map((q, i) => {
      const scoreVal = answers[i] ?? 0;
      const chosen = q.options.find((o) => o.score === scoreVal)?.label ?? "";
      return `Q${i + 1}: ${q.q} => ${chosen} (${scoreVal})`;
    }).join("\n");

    const quizData: QuizData = {
      answers: QUESTIONS.map((q, i) => ({
        q: i,
        value: answers[i] ?? 0,
      })),
      score,
      band: band.band,
    };

    const leadData: LeadFormData = {
      fullName: form.fullName,
      companyName: form.company,
      email: form.email,
      phone: "Not provided",
      country: countryName,
      industry: "Not specified",
      message: `Resilience Score quiz.\nScore: ${score}/24 — Band: ${band.band}\n\n${summary}`,
      pageSource: "Resilience Score Quiz",
      selectedPath: band.band,
      quiz: quizData,
    };

    const result = await submitLead(leadData, honeypot);
    setIsSubmitting(false);

    if (result.success) {
      trackFormSubmission("resilience_score_quiz", true);
      trackLeadSubmit("resilience_score_quiz");
      trackScoreLeadSubmit(band.band, score);
      trackConversion("resilience_score_request");

      if (result.quiz_id) {
        setPhase("report");
        setIsGeneratingReport(true);
        const reportResult = await generateResilienceReport(result.quiz_id);
        setIsGeneratingReport(false);

        if (reportResult.success && reportResult.summary) {
          setReport({
            summary: reportResult.summary,
            recommendations: reportResult.recommendations || [],
          });
        } else {
          setReportError(reportResult.error || "Could not generate your report.");
        }
      } else {
        setPhase("done");
      }
    } else {
      trackFormSubmission("resilience_score_quiz", false);
      toast({
        title: "Error",
        description: result.error || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <SEOHead
        title="Resilience Score | Skandor Technologies"
        description="Free 2-minute diagnostic across product, operations, and supply chain. Get your provisional Resilience Score instantly and a senior-reviewed scorecard within 1 business day."
        canonicalPath="/resources/resilience-score"
      />
      <Navbar />
      <main id="main-content" tabIndex={-1} className="min-h-screen focus:outline-none">
  
        <section className="relative overflow-hidden bg-tint pb-20 pt-32">
          <HeroFields compact />
          <div className="container relative z-10 mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tint border border-line text-steel-2 text-xs font-mono font-medium mb-5 uppercase tracking-[0.15em]">
                  <ShieldCheck className="w-3.5 h-3.5" /> Free diagnostic
                </div>
                <h1 className="text-[clamp(2rem,5vw,3.25rem)] font-semibold text-ink mb-5">
                  Where does your business stand across{" "}
                  <span className="text-steel-2">product, operations, and supply chain?</span>
                </h1>
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  8 questions. Under 2 minutes. Get your provisional Resilience Score
                  instantly. A senior partner reviews your answers and sends the full
                  scorecard within 1 business day.
                </p>
                <ul className="space-y-3 text-base text-ink-2">
                  {[
                    "Provisional score across three bands: Fragile, Developing, Resilient",
                    "Plain-language read on where the biggest gaps sit",
                    "Full senior-reviewed scorecard within 1 business day",
                    "Confidential. No pitch unless you ask for one.",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
  
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="surface-card p-6 md:p-8 shadow-[var(--shadow-card)]">
                  {/* Progress */}
                  {phase !== "done" && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-xs uppercase tracking-[0.15em] text-ink-3 font-semibold">
                          {phase === "quiz"
                            ? `Question ${step + 1} of ${QUESTIONS.length}`
                            : "Your provisional score"}
                        </span>
                        <span className="font-mono text-xs text-ink-3">
                          {phase === "quiz" ? `${progress}%` : "100%"}
                        </span>
                      </div>
                      <div
                        className="h-1.5 bg-tint rounded-full overflow-hidden"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={QUESTIONS.length}
                        aria-valuenow={phase === "quiz" ? step + 1 : QUESTIONS.length}
                        aria-label={
                          phase === "quiz"
                            ? `Question ${step + 1} of ${QUESTIONS.length}`
                            : "All questions complete"
                        }
                      >
                        <div
                          className="h-full bg-teal transition-all duration-300"
                          style={{ width: phase === "quiz" ? `${progress}%` : "100%" }}
                        />
                      </div>
                      {phase === "quiz" && (
                        <p className="mt-2 text-xs text-ink-3">
                          Each answer scores 0, 1, 2 or 3 points across {QUESTIONS.length}{" "}
                          questions — a {QUESTIONS.length * 3}-point scale. Your band is set by
                          total score — no weighting, no black box.
                        </p>
                      )}
                    </div>
                  )}
  
                  {phase === "quiz" && (
                    <AnimatePresence mode="wait">
                      <motion.section
                        key={step}
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -12 }}
                        transition={{ duration: 0.2 }}
                        aria-labelledby={`q-${step + 1}-title`}
                      >
                        <h2 id={`q-${step + 1}-title`} className="text-xl font-semibold text-ink mb-5 font-heading font-bold">
                          {QUESTIONS[step].q}
                        </h2>
                        <div className="space-y-2">
                          {QUESTIONS[step].options.map((opt, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => choose(i)}
                              className="w-full text-left px-4 py-3 rounded-lg border border-border bg-background hover:border-line hover:bg-teal/5 transition-colors text-base text-ink"
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        {step > 0 && (
                          <button
                            type="button"
                            onClick={back}
                            className="mt-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-ink"
                          >
                            <ArrowLeft className="w-4 h-4" /> Back
                          </button>
                        )}
                      </motion.section>
                    </AnimatePresence>
                  )}
  
                  {phase === "form" && (
                    <div>
                      <div className="mb-6 p-5 rounded-lg bg-tint border border-border">
                        <p className="font-mono text-xs uppercase tracking-[0.15em] text-ink-3 mb-2">
                          Provisional band
                        </p>
                        <p className={`font-heading font-bold text-[clamp(1.8rem,2.8vw,2.5rem)] font-semibold ${band.color} mb-2`}>
                          {band.band}{" "}
                          <span className="font-mono text-base text-ink-3 font-normal">
                            ({score}/24)
                          </span>
                        </p>
                        <p className="text-base text-ink-2 leading-relaxed">
                          {band.read}
                        </p>
                      </div>
  
                      <form onSubmit={handleSubmit}>
                        <h3 className="text-base font-semibold text-foreground mb-2">
                          Get your full scorecard
                        </h3>
                        <p className="text-sm text-muted-foreground mb-5">
                          A senior partner reviews your answers and sends the full
                          scorecard within 1 business day.
                        </p>
  
                        <div className="absolute -left-[9999px]" aria-hidden="true">
                          <Input
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={honeypot}
                            onChange={(e) => setHoneypot(e.target.value)}
                          />
                        </div>
  
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="rs-name">Full name *</Label>
                            <Input
                              id="rs-name"
                              value={form.fullName}
                              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                              className={errors.fullName ? "border-destructive" : ""}
                            />
                            {errors.fullName && (
                              <p className="text-destructive text-sm">{errors.fullName}</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="rs-company">Company *</Label>
                            <Input
                              id="rs-company"
                              value={form.company}
                              onChange={(e) => setForm({ ...form, company: e.target.value })}
                              className={errors.company ? "border-destructive" : ""}
                            />
                            {errors.company && (
                              <p className="text-destructive text-sm">{errors.company}</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="rs-email">Work email *</Label>
                            <Input
                              id="rs-email"
                              type="email"
                              value={form.email}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                              className={errors.email ? "border-destructive" : ""}
                            />
                            {errors.email && (
                              <p className="text-destructive text-sm">{errors.email}</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="rs-country">Country *</Label>
                            <CountrySelect
                              id="rs-country"
                              value={form.country}
                              onValueChange={(v) => setForm({ ...form, country: v })}
                              error={errors.country}
                            />
                          </div>
  
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="rs-agreed"
                              checked={agreed}
                              onCheckedChange={(c) => setAgreed(c as boolean)}
                            />
                            <Label
                              htmlFor="rs-agreed"
                              className="text-sm text-muted-foreground font-normal leading-relaxed"
                            >
                              I agree to the{" "}
                              <a href="/privacy" className="text-primary hover:underline">
                                Privacy Policy
                              </a>{" "}
                              and to receive my Resilience Scorecard by email.
                            </Label>
                          </div>
                          {errors.agreed && (
                            <p className="text-destructive text-sm">{errors.agreed}</p>
                          )}
  
                          <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5">
                            <Lock className="w-3 h-3" /> Confidential. Never shared.
                          </p>
  
                          <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="w-full"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <span className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Submitting...
                              </span>
                            ) : (
                              <>
                                Send me the full scorecard
                                <ArrowRight className="w-4 h-4" />
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </div>
                  )}
  
                  {phase === "report" && (
                    <div className="py-2">
                      {isGeneratingReport ? (
                        <div className="text-center py-10">
                          <div className="inline-flex p-4 rounded-full bg-tint mb-5">
                            <span className="w-8 h-8 border-2 border-line border-t-steel-2 rounded-full animate-spin" />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            Generating your diagnosis…
                          </h3>
                          <p className="text-base text-muted-foreground">
                            Our AI is writing a personalized read based on your answers.
                          </p>
                        </div>
                      ) : reportError ? (
                        <div className="text-center py-4">
                          <div className="inline-flex p-4 rounded-full bg-destructive/10 mb-5">
                            <CheckCircle2 className="w-10 h-10 text-destructive" />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">You're on the list.</h3>
                          <p className="text-base text-destructive mb-4">{reportError}</p>
                          <p className="text-base text-muted-foreground">
                            Your full senior-reviewed scorecard lands in{" "}
                            <span className="text-foreground font-medium">{form.email}</span>{" "}
                            within 1 business day.
                          </p>
                        </div>
                      ) : report ? (
                        <div className="space-y-6">
                          <div className="text-center">
                            <div className="inline-flex p-4 rounded-full bg-success/10 mb-5">
                              <CheckCircle2 className="w-10 h-10 text-success" />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground mb-3">
                              Your Resilience Diagnosis
                            </h3>
                            <div className="p-4 rounded-lg bg-tint border border-border mb-4 inline-block">
                              <p className="font-mono text-xs uppercase tracking-[0.15em] text-ink-3">
                                Your provisional band
                              </p>
                              <p className={`font-heading font-bold text-xl font-semibold ${band.color}`}>
                                {band.band}{" "}
                                <span className="font-mono text-base text-ink-3 font-normal">
                                  ({score}/24)
                                </span>
                              </p>
                            </div>
                          </div>
  
                          <div className="p-5 rounded-lg bg-background border border-border">
                            <p className="font-mono text-xs uppercase tracking-[0.15em] text-ink-3 mb-3">
                              AI diagnosis
                            </p>
                            <div className="prose prose-sm max-w-none text-base text-ink-2 leading-relaxed">
                              <ReactMarkdown>{report.summary}</ReactMarkdown>
                            </div>
                          </div>
  
                          <div className="p-5 rounded-lg bg-primary-light border border-primary/20">
                            <p className="font-mono text-xs uppercase tracking-[0.15em] text-[hsl(195_80%_26%)] mb-3">
                              Recommended next steps
                            </p>
                            <ol className="space-y-3 list-decimal list-inside text-base text-ink-2 leading-relaxed">
                              {report.recommendations.map((rec, i) => (
                                <li key={i} className="pl-1">
                                  <span className="ml-1">{rec}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
  
                          <p className="text-sm text-muted-foreground text-center">
                            A senior partner will review this and follow up at{" "}
                            <span className="text-foreground font-medium">{form.email}</span>{" "}
                            within 1 business day.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  )}
  
                  {phase === "done" && (
                    <div className="text-center py-4">
                      <div className="inline-flex p-4 rounded-full bg-success/10 mb-5">
                        <CheckCircle2 className="w-10 h-10 text-success" />
                      </div>
                      <h3 className="text-xl font-semibold text-foreground mb-3">You're on the list.</h3>
                      <div className="p-4 rounded-lg bg-tint border border-border mb-4 inline-block">
                        <p className="font-mono text-xs uppercase tracking-[0.15em] text-ink-3">
                          Your provisional band
                        </p>
                        <p className={`font-heading font-bold text-xl font-semibold ${band.color}`}>
                          {band.band} ({score}/24)
                        </p>
                      </div>
                      <p className="text-base text-muted-foreground">
                        Your full senior-reviewed scorecard lands in{" "}
                        <span className="text-foreground font-medium">{form.email}</span>{" "}
                        within 1 business day.
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
  
      </main>
      <Footer />
    </>
  );
};

export default ResilienceScore;
